import React from 'react';
import { Card, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { CardId } from './types';
import { DASHBOARD_CARD_TITLE_KEYS } from './dashboardCardKeys';

const { Text } = Typography;

interface BaseCardProps {
  children: React.ReactNode;
  cardId?: CardId;
  title?: React.ReactNode; // Optional override - if not provided, uses cardId to get from registry
  onClick?: () => void;
  style?: React.CSSProperties;
  bodyStyle?: React.CSSProperties;
  debugWidth?: number;
  debugHeight?: number;
}

export const BaseCard: React.FC<BaseCardProps> = ({
  children,
  cardId,
  title,
  onClick,
  style,
  bodyStyle,
  debugWidth,
  debugHeight,
}) => {
  const { t } = useTranslation();

  // Check if debug mode is enabled
  const debugEnv = process.env.DEBUG_CARD_DIMENSIONS;
  const isDebugMode = 
    debugEnv === 'true' || 
    debugEnv === true || 
    String(debugEnv).toLowerCase() === 'true';

  // Get title from registry if not provided and cardId is available
  // If title is explicitly null or undefined (passed as prop), don't use registry
  const getTitle = (): React.ReactNode => {
    // If title is explicitly provided (even if null/undefined), use it
    if (title !== undefined) return title || undefined;
    // Otherwise, get from registry if cardId is available
    if (cardId && DASHBOARD_CARD_TITLE_KEYS[cardId]) {
      return t(DASHBOARD_CARD_TITLE_KEYS[cardId]!);
    }
    return undefined;
  };

  // Standardize title rendering with consistent styling
  const renderTitle = () => {
    const titleToRender = getTitle();
    if (!titleToRender) return undefined;
    
    // Extract text content from ReactNode if possible, or use as-is
    let titleContent = titleToRender;
    
    // If title is a string, wrap it with standard styling
    if (typeof titleToRender === 'string') {
      titleContent = (
        <Text style={{ fontSize: '16px', fontWeight: 600, color: 'var(--color-text-dark, #042a2a)' }}>
          {titleToRender}
        </Text>
      );
    }
    
    // Add debug dimensions if enabled
    if (isDebugMode && (debugWidth !== undefined || debugHeight !== undefined)) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <div>{titleContent}</div>
          <Text
            style={{
              fontSize: '12px',
              color: 'var(--color-text-muted, #5a6a6a)',
              fontWeight: 400,
              fontFamily: 'monospace',
            }}
          >
            [{debugWidth || '?'} × {debugHeight || '?'}]
          </Text>
        </div>
      );
    }
    
    return titleContent;
  };

  const defaultStyle: React.CSSProperties = {
    borderRadius: '10px',
    boxShadow: '0px 0px 10px 0px #0000000d',
    border: '1px solid var(--color-border, #dbeae3)',
    height: '100%',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    ...(onClick && { cursor: 'pointer' }),
    ...style,
  };

  const {
    overflowY: bodyOverflowY,
    overflowX: bodyOverflowX,
    overflow: _bodyOverflow,
    ...restBodyStyle
  } = (bodyStyle ?? {}) as React.CSSProperties;

  const defaultBodyStyle: React.CSSProperties = {
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    ...restBodyStyle,
  };

  const titleText =
    typeof getTitle() === 'string' ? (getTitle() as string) : undefined;

  const scrollRegionStyle: React.CSSProperties = {
    flex: 1,
    minHeight: 0,
    overflowY: bodyOverflowY === 'hidden' ? 'hidden' : 'auto',
    overflowX: bodyOverflowX === 'hidden' ? 'hidden' : 'auto',
    outline: 'none',
  };

  return (
    <>
      <style>{`
        .base-card-title .ant-card-head-title {
          color: var(--color-text-dark, #042a2a) !important;
          font-size: 16px !important;
          font-weight: 600 !important;
        }
        .base-card-title .ant-card-head-title * {
          color: var(--color-text-dark, #042a2a) !important;
        }
      `}</style>
      <Card
        className="base-card-title"
        title={renderTitle()}
        style={defaultStyle}
        styles={{ body: defaultBodyStyle }}
        onClick={onClick}
      >
        <div
          tabIndex={0}
          role="region"
          aria-label={titleText}
          style={scrollRegionStyle}
        >
          {children}
        </div>
      </Card>
    </>
  );
};

